import { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Download, Calendar, DollarSign, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [categories, setCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    
    // Filter state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        category: '',
        supplier: '',
        document_number: ''
    });
    
    // Summary state
    const [summary, setSummary] = useState({ total: 0, count: 0, by_category: {} });

    useEffect(() => {
        loadExpenses();
        loadCategories();
    }, []);

    useEffect(() => {
        loadExpenses();
        loadSummary();
    }, [startDate, endDate]);

    const loadExpenses = async () => {
        try {
            setLoading(true);
            let url = '/expenses';
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (params.toString()) url += '?' + params.toString();
            
            const response = await api.get(url);
            setExpenses(response.data);
        } catch (error) {
            toast.error('Eroare la încărcarea cheltuielilor');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await api.get('/expenses/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadSummary = async () => {
        try {
            let url = '/expenses/summary';
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (params.toString()) url += '?' + params.toString();
            
            const response = await api.get(url);
            setSummary(response.data);
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    };

    const openModal = (expense = null) => {
        if (expense) {
            setEditingExpense(expense);
            setFormData({
                description: expense.description,
                amount: expense.amount.toString(),
                date: expense.date,
                category: expense.category || '',
                supplier: expense.supplier || '',
                document_number: expense.document_number || ''
            });
        } else {
            setEditingExpense(null);
            setFormData({
                description: '',
                amount: '',
                date: new Date().toISOString().split('T')[0],
                category: '',
                supplier: '',
                document_number: ''
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingExpense(null);
        setShowCategoryDropdown(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.description || !formData.amount || !formData.date) {
            toast.error('Completează toate câmpurile obligatorii');
            return;
        }

        try {
            const payload = {
                description: formData.description,
                amount: parseFloat(formData.amount),
                date: formData.date,
                category: formData.category || null,
                supplier: formData.supplier || null,
                document_number: formData.document_number || null
            };

            if (editingExpense) {
                await api.put(`/expenses/${editingExpense.id}`, payload);
                toast.success('Cheltuială actualizată');
            } else {
                await api.post('/expenses', payload);
                toast.success('Cheltuială adăugată');
            }
            
            closeModal();
            loadExpenses();
            loadSummary();
            loadCategories();
        } catch (error) {
            toast.error('Eroare la salvarea cheltuielii');
        }
    };

    const handleDelete = async (expense) => {
        if (!window.confirm(`Sigur dorești să ștergi "${expense.description}"?`)) return;
        
        try {
            await api.delete(`/expenses/${expense.id}`);
            toast.success('Cheltuială ștearsă');
            loadExpenses();
            loadSummary();
        } catch (error) {
            toast.error('Eroare la ștergere');
        }
    };

    const handleExport = async () => {
        try {
            let url = '/expenses';
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            
            const exportUrl = `/export/expenses?${params.toString()}`;
            
            const response = await api.get(exportUrl, { responseType: 'blob' });
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `cheltuieli_${startDate || 'all'}_${endDate || 'all'}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Raport exportat!');
        } catch (error) {
            toast.error('Eroare la export');
        }
    };

    const filteredExpenses = expenses
        .filter(exp => 
            exp.description?.toLowerCase().includes(search.toLowerCase()) ||
            exp.category?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));

    // Calculate supplier totals for ranking
    const supplierRanking = expenses
        .filter(exp => exp.supplier)
        .reduce((acc, exp) => {
            const supplier = exp.supplier;
            acc[supplier] = (acc[supplier] || 0) + exp.amount;
            return acc;
        }, {});

    const predefinedCategories = [
        'Achiziție paleți',
        'Transport paleți',
        'Contabilitate',
        'Taxe și impozite',
        'Utilități',
        'Chirie',
        'Salarii',
        'Marketing',
        'Altele'
    ];

    const allCategories = [...new Set([...predefinedCategories, ...categories])];

    return (
        <div className="min-h-screen bg-zinc-50 p-4 md:p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Cheltuieli
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">Gestionează cheltuielile firmei</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={handleExport}
                        variant="outline"
                        className="h-12 px-4"
                        data-testid="export-expenses-btn"
                    >
                        <Download size={20} className="mr-2" />
                        Export
                    </Button>
                    <Button
                        onClick={() => openModal()}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white h-12 px-6"
                        data-testid="add-expense-btn"
                    >
                        <Plus size={20} className="mr-2" />
                        Adaugă
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <DollarSign size={24} className="text-red-600" />
                        </div>
                        <div>
                            <p className="text-sm text-zinc-500">Total Cheltuieli</p>
                            <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {summary.total.toFixed(2)} RON
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-zinc-100 rounded-lg">
                            <Filter size={24} className="text-zinc-600" />
                        </div>
                        <div>
                            <p className="text-sm text-zinc-500">Nr. Înregistrări</p>
                            <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {summary.count}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <Calendar size={24} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-sm text-zinc-500">Categorie Top</p>
                            <p className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {Object.entries(summary.by_category || {}).sort((a,b) => b[1] - a[1])[0]?.[0] || '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
                        <Input
                            placeholder="Caută după descriere sau categorie..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 h-12"
                            data-testid="expense-search-input"
                        />
                    </div>
                    <div className="flex gap-2">
                        <div>
                            <Label className="text-xs text-zinc-500">De la</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-zinc-500">Până la</Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="h-10"
                            />
                        </div>
                        {(startDate || endDate) && (
                            <Button
                                variant="ghost"
                                onClick={() => { setStartDate(''); setEndDate(''); }}
                                className="self-end h-10"
                            >
                                Resetează
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Expenses List */}
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-zinc-500">Se încarcă...</div>
                ) : filteredExpenses.length === 0 ? (
                    <div className="p-8 text-center text-zinc-500">
                        {expenses.length === 0 ? 'Nu există cheltuieli înregistrate' : 'Nicio cheltuială găsită'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-zinc-50 border-b border-zinc-200">
                                <tr>
                                    <th className="text-center p-4 font-medium text-zinc-600 w-12">#</th>
                                    <th className="text-left p-4 font-medium text-zinc-600">Data</th>
                                    <th className="text-left p-4 font-medium text-zinc-600">Nr. Doc</th>
                                    <th className="text-left p-4 font-medium text-zinc-600">Descriere</th>
                                    <th className="text-left p-4 font-medium text-zinc-600">Furnizor</th>
                                    <th className="text-left p-4 font-medium text-zinc-600">Categorie</th>
                                    <th className="text-right p-4 font-medium text-zinc-600">Sumă</th>
                                    <th className="text-center p-4 font-medium text-zinc-600">Acțiuni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.map((expense, index) => (
                                    <tr key={expense.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                                        <td className="p-4 text-center text-zinc-500 font-medium">{index + 1}</td>
                                        <td className="p-4 text-zinc-900">{expense.date}</td>
                                        <td className="p-4 text-zinc-600 text-sm">{expense.document_number || '-'}</td>
                                        <td className="p-4 text-zinc-900 font-medium">{expense.description}</td>
                                        <td className="p-4 text-zinc-600">{expense.supplier || '-'}</td>
                                        <td className="p-4">
                                            {expense.category && (
                                                <span className="px-2 py-1 bg-zinc-100 text-zinc-700 rounded text-sm">
                                                    {expense.category}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-right font-bold text-red-600">
                                            {expense.amount.toFixed(2)} RON
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openModal(expense)}
                                                    className="p-2 text-zinc-600 hover:bg-zinc-100 rounded"
                                                    title="Editează"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(expense)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                                                    title="Șterge"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Category Breakdown */}
            {Object.keys(summary.by_category || {}).length > 0 && (
                <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-4">
                    <h3 className="font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Cheltuieli pe Categorii
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(summary.by_category)
                            .sort((a, b) => b[1] - a[1])
                            .map(([cat, amount]) => (
                                <div key={cat} className="p-3 bg-zinc-50 rounded-lg">
                                    <p className="text-sm text-zinc-500">{cat}</p>
                                    <p className="font-bold text-zinc-900">{amount.toFixed(2)} RON</p>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}

            {/* Supplier Ranking */}
            {Object.keys(supplierRanking).length > 0 && (
                <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-4">
                    <h3 className="font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        🏆 Clasament Furnizori
                    </h3>
                    <div className="space-y-2">
                        {Object.entries(supplierRanking)
                            .sort((a, b) => b[1] - a[1])
                            .map(([supplier, amount], index) => (
                                <div 
                                    key={supplier} 
                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                        index === 0 ? 'bg-amber-50 border border-amber-200' :
                                        index === 1 ? 'bg-zinc-100 border border-zinc-200' :
                                        index === 2 ? 'bg-orange-50 border border-orange-200' :
                                        'bg-zinc-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                            index === 0 ? 'bg-amber-400 text-white' :
                                            index === 1 ? 'bg-zinc-400 text-white' :
                                            index === 2 ? 'bg-orange-400 text-white' :
                                            'bg-zinc-300 text-zinc-700'
                                        }`}>
                                            {index + 1}
                                        </span>
                                        <span className="font-medium text-zinc-800">{supplier}</span>
                                    </div>
                                    <span className="font-bold text-red-600">{amount.toFixed(2)} RON</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {editingExpense ? 'Editează Cheltuială' : 'Adaugă Cheltuială'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="description">Descriere *</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Ex: Achiziție 2 paleți"
                                className="mt-1"
                                data-testid="expense-description-input"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="amount">Sumă (RON) *</Label>
                            <Input
                                id="amount"
                                type="number"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                placeholder="0.00"
                                className="mt-1"
                                data-testid="expense-amount-input"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="date">Data *</Label>
                            <Input
                                id="date"
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                className="mt-1"
                                data-testid="expense-date-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="supplier">Furnizor</Label>
                            <Input
                                id="supplier"
                                value={formData.supplier}
                                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                                placeholder="Ex: SC Exemplu SRL"
                                className="mt-1"
                                data-testid="expense-supplier-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="document_number">Nr. Document</Label>
                            <Input
                                id="document_number"
                                value={formData.document_number}
                                onChange={(e) => setFormData({ ...formData, document_number: e.target.value })}
                                placeholder="Ex: FAC-001234"
                                className="mt-1"
                                data-testid="expense-document-input"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="category">Categorie</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="category"
                                    value={formData.category}
                                    onChange={(e) => {
                                        setFormData({ ...formData, category: e.target.value });
                                        setShowCategoryDropdown(true);
                                    }}
                                    onFocus={() => setShowCategoryDropdown(true)}
                                    placeholder="Selectează sau scrie..."
                                    autoComplete="off"
                                    data-testid="expense-category-input"
                                />
                                {showCategoryDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {allCategories
                                            .filter(cat => !formData.category || cat.toLowerCase().includes(formData.category.toLowerCase()))
                                            .map(cat => (
                                                <button
                                                    key={cat}
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData({ ...formData, category: cat });
                                                        setShowCategoryDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 hover:bg-zinc-100 text-sm ${
                                                        formData.category === cat ? 'bg-zinc-100 font-medium' : ''
                                                    }`}
                                                >
                                                    {cat}
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeModal}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-zinc-900 hover:bg-zinc-800"
                            >
                                {editingExpense ? 'Salvează' : 'Adaugă'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Expenses;
